// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFormFrontSide.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartFormFrontSide component
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observe, observable, action } from 'mobx';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import reportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import EmbedReportStore from 'logic/Izenda.EmbedReportStore';
import ReportPartFormStore from 'logic/Izenda.ReportPartFormStore';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ReportPartCommonActionPopup from '../Izenda.ReportPartCommonActionPopup';
import VirtualFormFrontSide from './Izenda.VirtualFormFrontSide';
import ReportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';
import { get, flow, find, isNil, getOr } from 'lodash/fp';

const NUMBER_ZERO = 0;
const NUMBER_ONE = 1;

@connect()
export default class ReportPartFormFrontSide extends Component {
  static propTypes = {
    reportPart: PropTypes.object
  }

  @observable loadedData = false;
  @observable
  resultHTMLCode = {
    embeddedReports: [],
    embeddedSubReports: [],
    htmlContent: ''
  }

  triggerGenerateHTMLCode() {
    this.generateHTMLCode()
      .then( result => {
        this.resultHTMLCode = result;
      });
  }

  componentWillMount() {
    const { props, props: { reportPart } } = this;

    const reportPartContent = reportPart.reportPartContent;

    // all ready set data tree don't need to update again
    if (!reportPartContent.dataTree) {
      const dataTree = get('store.model.dataTree', props);

      if (dataTree) {
        reportPartContent.setDataTree(dataTree);
      } else {
        const reportId = reportPart.reportId;
        const formStore = new ReportPartFormStore(reportPart, get('store.reportStore.isLoadDraft', props));

        formStore.loadDataTree(reportId);
      }
    }

    this.embedReportStore = new EmbedReportStore();
    this.disposeObserveLoadDataSource = observe(
      reportPartContent.dataTree,
      'hasLoadDataSource',
      (change) => {
        change && this.triggerGenerateHTMLCode();
      }
    );
    this.disposeObserveLoadRecords = observe(
      reportPart,
      'previewDataRecords',
      () => {
        this.triggerGenerateHTMLCode();
      }
    );
  }

  componentDidMount() {
    this.generateHTMLCode()
      .then( result => {
        this.loadedData = true;
        this.resultHTMLCode = result;
      }
    );
  }

  componentWillUnmount() {
    this.props.refreshData && this.props.refreshData(false);
    this.props.loader.endLoading();
    this.disposeObserveLoadDataSource();
    this.disposeObserveLoadRecords();
  }

  componentDidUpdate() {
    const {
      props,
      props : { reportPart }
    } = this;

    const records = getOr([], 'previewDataRecords', reportPart);

    this.domNode &&
      $izenda(this.domNode)
        .off('click', '[data-hyperlinkaction=true]')
        .on('click', '[data-hyperlinkaction=true]', function(e) {
          e.stopPropagation();
          e.preventDefault();
          const $link = $izenda(e.currentTarget);
          const linkData = $link.data();
          const parse = flow(decodeURI, JSON.parse);
          const record = records[parseInt(linkData.recordindex, 10)];
          const selectedFieldName = parse(linkData.selectedfieldname);
          const isOpenCustomURLDirectly = linkData.isCustomURL;
          const isOpenEmbeddedJavascriptDirectly = linkData.isEmbeddedJavascript;

          const selectedField = find(function(field) {
            return field.fieldName === selectedFieldName;
          }, reportPart.fieldCollection);

          const fieldProperties = getOr({}, 'properties', selectedField);
          const subReport = get('subReport', fieldProperties);
          const customURL = get('customURL', fieldProperties);
          const embeddedJavascript = get('embeddedJavascript', fieldProperties);
          const numberOfPointActions = get('numberOfPointActions', selectedField);

          const fieldConfig = {
            subReport: subReport,
            customURL: customURL,
            embeddedJavascript: embeddedJavascript,
            numberOfPointActions: numberOfPointActions,
            fieldElement: selectedField
          };

          if (isOpenCustomURLDirectly) {
            ReportPartUtils.openCustomURL(props, customURL, [record]);
          } else if (isOpenEmbeddedJavascriptDirectly) {
            ReportPartUtils.performEmbeddedJavascript(props, customURL, [record]);
          } else {
            ReportPartUtils.openHyperlinkAction(props, fieldConfig, [record], ReportPartCommonActionPopup);
          }
        });

    this.domNode && this.updateStyle();
  }

  updateStyle() {
    this.updateTableStyle();
    this.updateContainerStyle();
  }

  updateTableStyle() {
    $izenda(this.domNode)
      .find('table')
      .each(function(index, item) {
        const padding = parseInt($izenda(item).attr('cellpadding'));
        const spacing = parseInt($izenda(item).attr('cellspacing'));

        $izenda(item).css('border-spacing', spacing);
        $izenda('th,td', item).css('padding', padding);
      });
  }

  updateContainerStyle() {
    const { embededReportPart, frontSidePartRef, reportPart } = this.props;
    if (embededReportPart && !isNil(frontSidePartRef)) {
      const containerHeight = reportPart.height * ReportBodyConstants.BODY_CELL_HEIGHT;
      const contentHeight = containerHeight > this.domNode.offsetHeight ? this.domNode.offsetHeight : containerHeight;
      frontSidePartRef.style.height = `${(contentHeight)}px`;
    }
  }

  generateHTMLCode() {
    const {
      props,
      props : {
        store,
        reportPart,
        reportPart : {
           reportPartContent,
           previewData
        }
      }
    }  = this;

    const reportId = get('reportId', store) || reportPart.reportId;
    const records = getOr([], 'previewDataRecords', reportPart);
    const fieldsMapping =  getOr([], 'fieldsMapping', previewData);
    const subTotalMapping =  getOr([], 'subTotalMapping', previewData);
    const grandTotalMapping =  getOr([], 'grandTotalMapping', previewData);

    return reportPartContent.generatePreviewData(
      props,
      fieldsMapping,
      subTotalMapping,
      grandTotalMapping,
      records,
      this.embedReportStore.model,
      reportId
    );
  }

  setDom = (dom) => {
    this.domNode = dom;
  }

  goToPage = (pageIndex: Number) => {
    const {
      props,
      props: {
        reportPart,
        previewRecords
      }
    } = this;
    props.needUpdatePreview(reportPart, null, pageIndex, null, null, previewRecords);
  }

  onPageSizeChange = (pageSize: Number) => {
    const {
      props,
      props: {
        reportPart,
        previewRecords,
        reportPart : { reportPartContent }
      }
    } = this;
    reportPartContent.changePageSize(pageSize);
    props.needUpdatePreview(reportPart, null, NUMBER_ONE, null, null, previewRecords);
  }

  render() {
    const {
      props,
      props: {
        viewMode,
        reportPart,
        reportPart : { reportPartContent, previewData },
        printingMode,
        finishedRender,
        embededReportPart,
        forDashboard
      }
  } = this;

    const properties = getOr({}, 'properties', reportPartContent);

    const formStyles = properties.isVisibleBorder
      ? {
          borderStyle: properties.borderStyle,
          borderColor: properties.borderColor,
          borderWidth: properties.borderWidth
        }
      : {};

    formStyles.backgroundColor = properties.backgroundColor;

    const paging = getOr({}, 'paging', previewData);
    const classes = classnames('u_clearBoth with-border', { hide: !properties.usePagination });

    // refresh data intervals
    props.refreshData && props.refreshData(true);

    return (
      <div ref={this.setDom}>
        <VirtualFormFrontSide
          embeddedReports={this.resultHTMLCode.embeddedReports}
          embeddedSubReports={this.resultHTMLCode.embeddedSubReports}
          masterDomNode={this.domNode}
          finishGetData={this.loadedData}
          viewHeight={reportPart.height * ReportBodyConstants.BODY_CELL_HEIGHT}
          printingMode={printingMode}
          finishedRender={finishedRender}
          embededReportPart={embededReportPart}
          viewMode={viewMode}
          forDashboard={forDashboard}
          ignoreCache={props.ignoreCache}
        >
          <div
            className='report-part-form-front custom-tinymce izenda-ResetCssForm'
            style={formStyles}
            dangerouslySetInnerHTML={{ __html: this.resultHTMLCode.htmlContent }}
          />
        </VirtualFormFrontSide>
        {!printingMode && (
          <Paginator
            onChange={this.goToPage}
            onDropdownChange={this.onPageSizeChange}
            totalRecords={paging.total || NUMBER_ZERO}
            pageSize={paging.pageSize || reportDesignerConstants.DEFAULT_PAGE_SIZE}
            pageIndex={paging.pageIndex || reportDesignerConstants.DEFAULT_PAGE_INDEX}
            className={classes}
            canChangePageSize={!viewMode}
          />
        )}
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartForm/Izenda.ReportPartFormFrontSide.js